import moment from "moment";
import {
	monthlyTypes,
	typeKey,
	trainingTypePartial,
	typeColor,
	trainingTypeColors,
} from "./trainingTypes";
export function appendTrainingTypesPDF(
	doc,
	data,
	scope,
	metric,
	clean,
	firstPageEmpty,
) {
	const aggregate = data.trainingAnalysis;
	if (!aggregate) return null;
	const analysis = aggregate;
	const margin = 12,
		width = doc.internal.pageSize.getWidth() - 24;
	let headerScope = scope;
	let y = 0,
		startPage;
	const header = (title) => {
		if (!firstPageEmpty || startPage) doc.addPage();
		firstPageEmpty = false;
		if (!startPage) startPage = doc.getNumberOfPages();
		doc.setTextColor(23, 57, 95);
		doc.setFont("Matrix", "bold");
		doc.setFontSize(12);
		doc.text(clean(data.rosterName + " | " + title), margin, 12);
		doc.setFont("Matrix", "normal");
		doc.setFontSize(8);
		doc.text(
			clean(
				"Stagione " +
					data.seasonName +
					" | " +
					(headerScope === "scheduled"
						? "Sedute programmate"
						: "Sedute concluse"),
			),
			margin,
			18,
		);
		y = 25;
	};
	const text = (value) => {
		doc.setFont("Matrix", "normal");
		doc.setFontSize(8);
		doc.setTextColor(84, 106, 133);
		const lines = doc.splitTextToSize(clean(value), width);
		if (y + lines.length * 4 > 185) header("Tipologie allenamento - continua");
		doc.text(lines, margin, y);
		y += lines.length * 4 + 3;
	};
	const table = (labels, widths, rows, accents = []) => {
		const draw = (values, heading, accent) => {
			doc.setFontSize(8);
			doc.setFont("Matrix", heading ? "bold" : "normal");
			const lines = values.map((value, index) =>
				doc.splitTextToSize(clean(value), widths[index] - 4),
			);
			const height = Math.max(6, ...lines.map((line) => line.length * 3.4 + 3));
			if (y + height > 185) {
				header("Tipologie allenamento - continua");
				draw(labels, true);
			}
			let x = margin;
			values.forEach((value, index) => {
				doc.setFillColor(...(heading ? [240, 245, 251] : [255, 255, 255]));
				doc.setDrawColor(220, 229, 239);
				doc.setLineWidth(0.15);
				doc.rect(x, y, widths[index], height, "FD");
				doc.setTextColor(23, 57, 95);
				doc.text(lines[index], x + 2, y + 4);
				x += widths[index];
			});
			if (accent) {
				doc.setFillColor(accent);
				doc.rect(margin, y, 0.8, height, "F");
			}
			y += height;
		};
		draw(labels, true);
		rows.forEach((row, index) => draw(row, false, accents[index]));
		y += 5;
	};
	const number = (value) =>
		value == null ? "-" : value.toLocaleString("it-IT");
	const summary = analysis.period[scope];
	header("Tipologie allenamento");
	text(
		moment(data.fromDate).format("DD/MM/YYYY") +
			" - " +
			moment(data.toDate).format("DD/MM/YYYY") +
			" | " +
			summary.sessions +
			" sedute | " +
			summary.exercises +
			" esercizi | " +
			number(summary.minutes) +
			" minuti registrati.",
	);
	table(
		["Tipologia", "Sedute", "Esercizi", "Minuti", "% minuti noti"],
		[width * 0.44, width * 0.13, width * 0.13, width * 0.15, width * 0.15],
		summary.types.map((type) => [
			type.type,
			type.sessions,
			type.exercises,
			number(type.minutes) +
				(type.missingDuration || type.unknownAttendance ? " *" : ""),
			type.minutes != null && summary.minutes > 0
				? Math.round((100 * type.minutes) / summary.minutes) + "%"
				: "-",
		]),
	);
	if (!summary.types.length)
		text(
			summary.sessions
				? "Esercizi non inseriti: tipologie e minuti non rilevati."
				: "Nessuna seduta nel periodo.",
		);
	text(
		"Da completare: " +
			(summary.unknownAttendance || 0) +
			" presenze mancanti/da verificare; " +
			summary.sessionsWithoutExercises +
			" sedute senza esercizi; " +
			summary.missingDuration +
			" esercizi senza durata; " +
			summary.withoutType +
			" senza tipologia. * Durata parziale o non rilevata.",
	);
	text(
		"Una seduta puo contenere piu tipologie: non sommare le sedute per tipo per ottenere le sedute totali. Minuti degli esercizi registrati, non durata calendario o minutaggio individuale. Riepilogo della rosa indipendente dalla ricerca giocatore.",
	);
	headerScope = scope;
	header(
		scope === "scheduled"
			? "Riepilogo esercizi programmati"
			: "Riepilogo esercizi svolti",
	);
	text(
		moment(data.fromDate).format("DD/MM/YYYY") +
			" - " +
			moment(data.toDate).format("DD/MM/YYYY") +
			(scope === "scheduled"
				? " | Sedute programmate"
				: " | Solo sedute concluse") +
			"; il riepilogo non e limitato dalla ricerca giocatore.",
	);
	const exerciseColors = trainingTypeColors(aggregate);
	const exercises =
		(scope === "scheduled"
			? aggregate.scheduledExercises
			: aggregate.exercises) || [];
	table(
		["Esercizio", "Tipologia", metric === "minutes" ? "Minuti" : "Sedute"],
		[width * 0.57, width * 0.27, width * 0.16],
		exercises.map((e) => [
			e.name,
			e.type,
			number(e[metric]) +
				(metric === "minutes" ? " min" : "") +
				(metric === "minutes" && e.missingDuration ? " *" : ""),
		]),
		exercises.map((e) =>
			typeColor({type: e.type, unclassified: e.unclassified}, exerciseColors),
		),
	);
	if (!exercises.length)
		text("Nessun esercizio inserito nelle sedute dello stato selezionato.");
	text(
		"Sedute: numero di allenamenti distinti in cui compare l'esercizio. Minuti: somma delle durate registrate. * Durata parziale o non rilevata; esercizi con la stessa anagrafica raggruppati per ID.",
	);
	headerScope = scope;
	header("Confronto tipologie tra mesi");
	text(
		"Intera stagione | " +
			(metric === "minutes"
				? "Minuti degli esercizi"
				: "Sedute contenenti la tipologia") +
			" | indipendente dal periodo della matrice.",
	);
	const monthly = monthlyTypes(analysis, scope, metric);
	const monthWidth = (width - 57) / Math.max(1, monthly.rows.length);
	table(
		[
			"Tipologia",
			...monthly.rows.map((month) => moment(month.month).format("MMM YY")),
		],
		[57, ...monthly.rows.map(() => monthWidth)],
		monthly.types.map((type) => [
			type.type,
			...monthly.rows.map(
				(month) =>
					number(month[typeKey(type)]) +
					(trainingTypePartial(month.summary, type, metric) ? " *" : ""),
			),
		]),
	);
	if (!monthly.types.length)
		text(
			"Nessuna tipologia registrata nella stagione per lo stato selezionato.",
		);
	text(
		"Zero: nessuna attivita di quel tipo registrata. -: dato non rilevato; * dati parziali. Le sedute con piu tipi compaiono in piu righe." +
			"",
	);
	const populated = monthly.rows.filter(
		(month) =>
			month.summary.sessions > 0 || month.summary.unknownAttendance > 0,
	);
	if (populated.length)
		table(
			[
				"Mese",
				"Sedute",
				"Senza esercizi",
				"Durate mancanti",
				"Presenze da verificare",
			],
			[width * 0.28, width * 0.18, width * 0.18, width * 0.18, width * 0.18],
			populated.map((month) => [
				moment(month.month).format("MMMM YYYY"),
				month.summary.sessions,
				month.summary.sessionsWithoutExercises,
				month.summary.missingDuration,
				month.summary.unknownAttendance || 0,
			]),
		);
	return startPage;
}
